import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { XCircle, CheckCircle2, Loader2, Calendar, Clock, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AppointmentCancel() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [cancelled, setCancelled] = useState(false);
  const [cancelEmailSent, setCancelEmailSent] = useState(false);

  // Fetch booking data (by cancellation_token)
  const { data: booking, isLoading, error } = useQuery(
    ['appointments', 'cancel', token],
    async () => {
      const response = await api.get(`/appointments/public/confirm/${token}`);
      return response.data.data;
    },
    { enabled: !!token, retry: 1 }
  );

  // Storno anfordern (sendet E-Mail an Eltern; Bestätigung per Link erforderlich)
  const cancelMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/cancel/${token}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setCancelEmailSent(true);
        toast.success(data?.message || 'E-Mail zur Bestätigung der Stornierung wurde versendet.');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Stornierung');
      }
    }
  );

  useEffect(() => {
    if (booking && booking.status === 'cancelled') {
      setCancelled(true);
    }
  }, [booking]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Buchung nicht gefunden</h1>
          <p className="text-gray-600 mb-4">
            Die angeforderte Buchung existiert nicht oder kann nicht storniert werden.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (cancelled || booking.status === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Termin storniert!</h1>
          <p className="text-gray-600 mb-4">
            Ihr Termin wurde erfolgreich storniert.
          </p>
          <p className="text-sm text-gray-500">
            Sie werden zur Übersicht weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  if (cancelEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">E-Mail versendet</h1>
          <p className="text-gray-600 mb-4">
            Eine E-Mail zur Bestätigung der Stornierung wurde an die hinterlegte E-Mail-Adresse gesendet. Die Stornierung muss innerhalb von 30 Minuten über den Link in der E-Mail bestätigt werden. Danach verfällt die Anfrage und der Termin bleibt bestehen.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (booking.status !== 'confirmed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Termin kann nicht storniert werden</h1>
          <p className="text-gray-600 mb-4">
            Dieser Termin kann nicht mehr storniert werden.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">Termin stornieren</h1>
        
        <div className="bg-gray-50 rounded p-4 mb-6">
          <div className="mb-3">
            <div className="text-sm text-gray-600 mb-1">Termin</div>
            <div className="font-semibold">
              {format(parseISO(booking.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} -{' '}
              {format(parseISO(booking.end_time), 'HH:mm', { locale: de })}
            </div>
          </div>
          <div className="mb-3">
            <div className="text-sm text-gray-600 mb-1">Lehrer</div>
            <div className="font-semibold">
              {booking.teacher_first_name} {booking.teacher_last_name}
            </div>
          </div>
          {booking.student_name && (
            <div>
              <div className="text-sm text-gray-600 mb-1">Schüler</div>
              <div className="font-semibold">{booking.student_name}</div>
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-6 text-sm text-yellow-800">
          An die hinterlegte E-Mail-Adresse wird ein Bestätigungslink gesendet. Die Stornierung wird erst nach Klick auf den Link wirksam (gültig 30 Minuten).
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => navigate('/appointments/all')}
            className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isLoading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {cancelMutation.isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird storniert...
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5" />
                Termin stornieren
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


